/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import MapComponent from '../components/MapComponent';
import CameraCapture from '../components/CameraCapture';
import moondreamService from '../services/moondreamService';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';
import { mapLotUnitForBackend, normalizeLotUnitForForm } from '../utils/formatters';

const MAX_PROPERTY_PHOTOS = 20;
const MIN_PROPERTY_PHOTOS = 3;

const PropertyReportPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  
  const [step, setStep] = useState('location'); // 'location', 'camera', 'form'
  const [mediaFiles, setMediaFiles] = useState([]);
  const [photoStep, setPhotoStep] = useState('sale_sign'); // 'sale_sign', 'property_photos', 'optional_video'
  const [saleSignPhoto, setSaleSignPhoto] = useState(null);
  const [propertyPhotos, setPropertyPhotos] = useState([]);
  const [optionalVideo, setOptionalVideo] = useState(null);
  const [optionalVideoPreview, setOptionalVideoPreview] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictProperty, setConflictProperty] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [userLocation, setUserLocation] = useState(null); // Manual location set by user
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [selectedLocationMarker, setSelectedLocationMarker] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [focusOnLocation, setFocusOnLocation] = useState(null);
  const [locationSource, setLocationSource] = useState('gps'); // 'gps' or 'user'
  const [gpsAddress, setGpsAddress] = useState(null);
  const [isGeocodingGPS, setIsGeocodingGPS] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isGeocodingSelected, setIsGeocodingSelected] = useState(false);
  const [saleSignPreview, setSaleSignPreview] = useState(null);
  const [propertyPhotoPreviews, setPropertyPhotoPreviews] = useState([]);
  const [localPropertyReference] = useState(() => `LP-${Math.floor(100000 + Math.random() * 900000)}`);
  // eslint-disable-next-line no-unused-vars
  const locationRequestInProgress = useRef(false);
  
  // Moondream AI analysis states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  /** When true, the optional form section (contact, dimensions, price) is shown; toggled by "Weiter" button */
  const [formOptionalExpanded, setFormOptionalExpanded] = useState(false);
  const optionalFormSectionRef = useRef(null);

  const requiredFieldClass = (isFilled) => (isFilled
    ? 'ring-2 ring-green-300 ring-offset-2 ring-offset-white'
    : 'ring-2 ring-red-300 ring-offset-2 ring-offset-white');

  // Mobile: compact header (only back button)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  
  // Form data
  const [formData, setFormData] = useState({
    propertyCategory: 'land_lot',
    saleType: 'for_sale',
    isOwner: 'no',
    contactName: '',
    phone: '',
    email: '',
    whatsapp: '',
    hasTitle: 'yes',
    plotNumber: '',
    roadAccess: '',
    roadSurface: '',
    inCommunity: '',
    waterConnection: '',
    electricity: '',
    lotSizeValue: '',
    lotSizeUnit: 'sqm',
    livingAreaValue: '',
    livingAreaUnit: 'sqm',
    bedrooms: '0',
    bathrooms: '0',
    yearBuilt: '',
    price: '',
    currency: 'USD',
    description: '',
    allowAgentContact: null, // null = not asked, true/false = answer (only when for_sale_by_owner + isOwner yes)
  });

  useEffect(() => {
    console.log('PropertyReportPage: useEffect running');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('user:', user);
    console.log('user role:', user?.role);
    
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login');
      navigate('/login', { state: { role: 'land_reporter' } });
      return;
    }
    
    if (user?.role !== 'land_reporter') {
      console.log('Wrong role:', user?.role, 'redirecting to login');
      navigate('/login', { state: { role: 'land_reporter' } });
      return;
    }
    
    // Redirect to onboarding if profile is incomplete
    if (user?.needs_profile_completion || !user?.address || !user?.phone) {
      console.log('Profile incomplete, redirecting to onboarding');
      navigate('/onboarding/reporter');
      return;
    }
    
    console.log('All checks passed, staying on page');
  }, [isAuthenticated, user, navigate]);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const url = `${apiUrl('/geocode/reverse')}?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const json = await res.json();
      if (json && json.success) return json.data;
      return null;
    } catch (e) {
      return null;
    }
  }, []);

  // Track latest user-selected location to avoid stale closures inside geolocation callbacks
  const userLocationRef = useRef(null);
  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  const getCurrentLocation = useCallback(() => {
    console.log('getCurrentLocation called');
    
    // Prevent multiple simultaneous calls
    if (locationRequestInProgress.current) {
      console.log('getCurrentLocation already in progress, skipping');
      return;
    }
    
    locationRequestInProgress.current = true;
    setIsGettingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = { lat: latitude, lng: longitude };
          setCurrentLocation(location);
          setGpsLocation(location);
          // If no user location set yet, use GPS as selected location (use ref to avoid stale closure)
          if (!userLocationRef.current) {
            setSelectedLocation(location);
            setSelectedLocationMarker({
              id: 'property-location',
              lat: location.lat,
              lng: location.lng,
              title: t('reportPage.map.markerTitle'),
              description: t('reportPage.map.markerDescGps'),
              color: '#EF4444'
            });
            setFocusOnLocation(location);
            setLocationSource('gps');
          }
          setIsGettingLocation(false);
          locationRequestInProgress.current = false;
          toast.success(t('reportPage.toasts.locationFound'), { icon: '📍' });
          // Kick off reverse geocoding for GPS location
          setIsGeocodingGPS(true);
          reverseGeocode(latitude, longitude).then((addr) => {
            setGpsAddress(addr);
          }).finally(() => setIsGeocodingGPS(false));
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsGettingLocation(false);
          locationRequestInProgress.current = false;
          
          let errorMessage = t('reportPage.toasts.locationErrorGeneric');
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = t('reportPage.toasts.locationPermissionDenied');
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = t('reportPage.toasts.locationUnavailable');
              break;
            case error.TIMEOUT:
              errorMessage = t('reportPage.toasts.locationTimeout');
              break;
            default:
              errorMessage = t('reportPage.toasts.locationUnknown');
              break;
          }
          
          toast.error(errorMessage, {
            duration: 5000,
            icon: '📍',
          });
          
          // Show additional help for permission denied
          if (error.code === error.PERMISSION_DENIED) {
            setTimeout(() => {
              toast(t('reportPage.toasts.locationTipEnable'), {
                duration: 8000,
                icon: '💡',
              });
            }, 2000);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // Increased timeout
          maximumAge: 300000 // 5 minutes
        }
      );
    } else {
      setIsGettingLocation(false);
      locationRequestInProgress.current = false;
      toast.error(t('reportPage.toasts.geoNotSupported'));
    }
  }, [userLocation, reverseGeocode]);

  // Auto-get location when component mounts
  useEffect(() => {
    console.log('Location useEffect running:', {
      isAuthenticated,
      userRole: user?.role,
      currentLocation: !!currentLocation,
      isGettingLocation,
      locationRequested,
      locationRequestInProgress: locationRequestInProgress.current
    });
    
    if (isAuthenticated && user?.role === 'land_reporter' && !currentLocation && !locationRequestInProgress.current && !locationRequested) {
      console.log('Requesting location...');
      setLocationRequested(true);
      getCurrentLocation();
    }
  }, [isAuthenticated, user?.role, currentLocation, isGettingLocation, locationRequested, getCurrentLocation]);

  // Auto-set GPS location as selected location when found (without overriding user-chosen location)
  useEffect(() => {
    if (
      currentLocation &&
      !selectedLocation && // don't override an existing selection
      !userLocation && // respect manually picked locations
      step === 'location'
    ) {
      setSelectedLocation(currentLocation);
      setSelectedLocationMarker({
        id: 'property-location',
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        title: t('reportPage.map.markerTitle'),
        description: t('reportPage.map.markerDescGps'),
        color: '#EF4444'
      });
      setFocusOnLocation(currentLocation);
      setLocationSource('gps');
    }
  }, [currentLocation, step, selectedLocation, userLocation]);

  // Reverse geocode when selected location changes
  useEffect(() => {
    if (selectedLocation) {
      setIsGeocodingSelected(true);
      reverseGeocode(selectedLocation.lat, selectedLocation.lng).then((addr) => {
        setSelectedAddress(addr);
      }).finally(() => setIsGeocodingSelected(false));
    } else {
      setSelectedAddress(null);
    }
  }, [selectedLocation, reverseGeocode]);

  // Update media files whenever sale sign photo, property photos, or video changes
  const updateMediaFiles = useCallback(() => {
    const allFiles = [];
    if (saleSignPhoto) allFiles.push(saleSignPhoto);
    allFiles.push(...propertyPhotos);
    if (optionalVideo) allFiles.push(optionalVideo);
    setMediaFiles(allFiles);
  }, [saleSignPhoto, propertyPhotos, optionalVideo]);

  useEffect(() => {
    updateMediaFiles();
  }, [saleSignPhoto, propertyPhotos, optionalVideo, updateMediaFiles]);

  useEffect(() => {
    let cancelled = false;
    let objectUrlToRevoke = null;

    if (!saleSignPhoto) {
      setSaleSignPreview(null);
      return () => null;
    }

    const applyPreview = (value) => {
      if (!cancelled) {
        setSaleSignPreview(value);
      }
    };

    if (typeof FileReader !== 'undefined') {
      const reader = new FileReader();
      reader.onloadend = () => applyPreview(reader.result);
      reader.onerror = () => {
        const fallbackUrl = URL.createObjectURL(saleSignPhoto);
        objectUrlToRevoke = fallbackUrl;
        applyPreview(fallbackUrl);
      };
      reader.readAsDataURL(saleSignPhoto);
    } else {
      const fallbackUrl = URL.createObjectURL(saleSignPhoto);
      objectUrlToRevoke = fallbackUrl;
      applyPreview(fallbackUrl);
    }

    return () => {
      cancelled = true;
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [saleSignPhoto]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    if (!propertyPhotos.length) {
      setPropertyPhotoPreviews([]);
      return () => null;
    }

    const createPreview = (file) => new Promise((resolve) => {
      if (typeof FileReader !== 'undefined') {
        const reader = new FileReader();
        reader.onloadend = () => resolve({ file, preview: reader.result });
        reader.onerror = () => {
          const fallbackUrl = URL.createObjectURL(file);
          objectUrls.push(fallbackUrl);
          resolve({ file, preview: fallbackUrl });
        };
        reader.readAsDataURL(file);
      } else {
        const fallbackUrl = URL.createObjectURL(file);
        objectUrls.push(fallbackUrl);
        resolve({ file, preview: fallbackUrl });
      }
    });

    const generatePreviews = async () => {
      const previews = await Promise.all(propertyPhotos.map(createPreview));
      if (!cancelled) {
    setPropertyPhotoPreviews(previews);
      }
    };

    generatePreviews();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [propertyPhotos]);

  useEffect(() => {
    if (!optionalVideo) {
      setOptionalVideoPreview(null);
      return;
    }
    const previewUrl = URL.createObjectURL(optionalVideo);
    setOptionalVideoPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [optionalVideo]);

  const handleMediaCapture = (file) => {
    console.log('Media captured:', file);

    if (!file) {
      toast.error(t('reportPage.toasts.noMediaCaptured'));
      return;
    }

    const fileType = file.type || '';
    const isImage = fileType.startsWith('image/');
    const isVideo = fileType.startsWith('video/');

    if (photoStep === 'sale_sign') {
      if (!isImage) {
        toast.error(t('reportPage.toasts.captureSaleSignPhoto'));
        return;
      }

      setSaleSignPhoto(file);
      setPhotoStep('property_photos');
      toast.success(t('reportPage.toasts.saleSignCapturedAnalyzing'));

      // Automatically analyze the sale sign photo
      setTimeout(() => {
        analyzeSaleSign(file);
      }, 500); // Small delay to ensure state is updated
      return;
    }

    if (isImage) {
      if (propertyPhotos.length >= MAX_PROPERTY_PHOTOS) {
        toast.error(t('reportPage.toasts.maxPropertyPhotos', { max: MAX_PROPERTY_PHOTOS }));
        return;
      }

      const newCount = propertyPhotos.length + 1;
      const remainingToMinimum = Math.max(MIN_PROPERTY_PHOTOS - newCount, 0);
      const remainingCapacity = Math.max(MAX_PROPERTY_PHOTOS - newCount, 0);

      setPropertyPhotos(prev => [...prev, file]);

      if (newCount < MIN_PROPERTY_PHOTOS) {
        toast.success(t('reportPage.toasts.propertyPhotoCapturedNeedMore', { count: newCount, remaining: remainingToMinimum }));
      } else if (newCount === MIN_PROPERTY_PHOTOS) {
        toast.success(t('reportPage.toasts.propertyPhotoCapturedMinimumReached', { count: newCount, remainingCapacity }));
      } else {
        toast.success(t('reportPage.toasts.propertyPhotoCapturedExtra', { count: newCount, remainingCapacity, max: MAX_PROPERTY_PHOTOS }));
      }

      if (photoStep !== 'property_photos') {
        setPhotoStep('property_photos');
      }

      return;
    }

    if (isVideo) {
      setOptionalVideo(file);
      toast.success(t('reportPage.toasts.videoCaptured'));
      setPhotoStep('complete');
      return;
    }

    toast.error(t('reportPage.toasts.unsupportedMedia'));
  };

  const skipVideo = () => {
    setOptionalVideo(null);
    setPhotoStep('complete');
    toast.success(t('reportPage.toasts.videoSkipped'));
  };

  const hasMinimumPropertyPhotos = propertyPhotos.length >= MIN_PROPERTY_PHOTOS;
  const canAddMorePropertyPhotos = propertyPhotos.length < MAX_PROPERTY_PHOTOS;

  const openPropertyFormStep = () => {
    setFormOptionalExpanded(false);
    setStep('form');
  };

  const handleSkipSaleSign = () => {
    setPhotoStep('property_photos');
    toast(t('reportPage.toasts.skipSaleSignContinue'), { icon: '📷' });
  };

  const proceedToLocation = () => {
    if (propertyPhotos.length < MIN_PROPERTY_PHOTOS) {
      toast.error(t('reportPage.toasts.takeMinPropertyPhotos', { min: MIN_PROPERTY_PHOTOS }));
      return;
    }
    openPropertyFormStep();
  };

  const handleRemoveOptionalVideo = () => {
    setOptionalVideo(null);
    setPhotoStep('optional_video');
    toast(t('reportPage.toasts.optionalVideoRemoved'), { icon: '🗑️' });
  };

  const isResidentialProperty = formData.propertyCategory === 'apartment' || formData.propertyCategory === 'house';
  const requiresOwnerFlag = formData.saleType === 'for_sale_by_owner';
  const showOptionalFields = useMemo(() => {
    const hasPhoneOrWhatsapp = (String(formData.phone ?? '').trim().length > 0) || (String(formData.whatsapp ?? '').trim().length > 0);
    const hasSaleType = Boolean(formData.saleType);
    const hasPropertyCategory = Boolean(formData.propertyCategory);
    const hasOwner = !requiresOwnerFlag || Boolean(formData.isOwner);

    return hasPhoneOrWhatsapp
      && hasSaleType
      && hasPropertyCategory
      && hasOwner;
  }, [
    formData.phone,
    formData.whatsapp,
    formData.saleType,
    formData.propertyCategory,
    formData.isOwner,
    requiresOwnerFlag,
  ]);

  const aiData = analysisResult?.extractedData || {};
  const aiChips = [];
  const addAiChip = (field, label, value, extra = {}) => {
    if (value === null || value === undefined) return;

    let payloadValue = value;
    if (Array.isArray(payloadValue)) {
      payloadValue = payloadValue.filter(Boolean).join(', ');
    } else if (typeof payloadValue === 'object') {
      payloadValue = JSON.stringify(payloadValue);
    }

    if (payloadValue === null || payloadValue === undefined) return;
    const stringValue = payloadValue.toString().trim();
    if (!stringValue) return;

    const normalizedCurrency = extra.currency ? normalizeCurrency(extra.currency) : null;
    const displayValue = extra.displayValue
      || (extra.unit ? `${stringValue} ${extra.unit}`
        : normalizedCurrency ? `${normalizedCurrency} ${stringValue}`
          : stringValue);

    const normalizedChip = {
      field,
      label,
      value: payloadValue,
      unit: extra.unit,
      currency: normalizedCurrency,
      displayValue: displayValue.toString().trim() || stringValue,
    };

    aiChips.push(normalizedChip);
  };

  addAiChip('sale_type', t('reportPage.aiFields.saleType'), aiData.sale_type);
  addAiChip('property_category', t('reportPage.aiFields.propertyType'), aiData.property_type);
  addAiChip('contact_name', t('reportPage.aiFields.contactName'), aiData.contact_name);
  addAiChip('email', t('reportPage.aiFields.email'), aiData.email);
  addAiChip('price', t('reportPage.aiFields.price'), aiData.price_value ?? aiData.price, { currency: aiData.price_currency || aiData.currency });
  if (Array.isArray(aiData.phone_numbers)) {
    aiData.phone_numbers.forEach((num) => addAiChip('phone', t('reportPage.aiFields.phoneNumber'), num));
  } else {
    addAiChip('phone', t('reportPage.aiFields.phoneNumber'), aiData.phone_number);
  }
  addAiChip('lot_size', t('reportPage.aiFields.lotSize'), aiData.lot_size, { unit: normalizeLotUnitForForm(aiData.size_unit) });
  addAiChip('description', t('reportPage.aiFields.description'), aiData.description || aiData.notable_features?.join(', ') || aiData.summary);
  addAiChip('address', t('reportPage.aiFields.address'), aiData.address);

  const handleRemoveSaleSignPhoto = () => {
    setSaleSignPhoto(null);
    setAnalysisResult(null);
    setAnalysisError(null);
    setPhotoStep('sale_sign');
    toast(t('reportPage.toasts.saleSignRemovedRetake'), {
      icon: '🗑️',
    });
  };

  const handleRemovePropertyPhoto = (index) => {
    setPropertyPhotos((prev) => {
      const updated = prev.filter((_, idx) => idx !== index);
      if (updated.length < MIN_PROPERTY_PHOTOS && (photoStep === 'optional_video' || photoStep === 'complete')) {
        setPhotoStep('property_photos');
      }
      return updated;
    });
    toast(t('reportPage.toasts.propertyPhotoRemoved'), { icon: '🗑️' });
  };

  // Analyze sale sign photo only
  const analyzeSaleSign = async (photoFile = null) => {
    const photoToAnalyze = photoFile || saleSignPhoto;
    console.log('Starting sale sign analysis...');
    console.log('Sale sign photo:', photoToAnalyze);
    
    if (!photoToAnalyze) {
      toast.error(t('reportPage.toasts.takeSaleSignFirst'));
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResult(null);

    try {
      toast.loading(t('reportPage.toasts.analyzingSaleSign'), { id: 'analysis', icon: '🤖' });

      // Upload sale sign photo
      const saleSignFormData = new FormData();
      saleSignFormData.append('image', photoToAnalyze);
      saleSignFormData.append('prompt', 'Extract all information from this property sale sign. The sign may be in any language (English, Spanish, French, German, Portuguese, etc.). Look for: 1) Sale type (For Sale/For Rent/For Lease), 2) Price (monetary value only, no units), 3) Phone number, 4) Email address, 5) WhatsApp number, 6) Lot size/acreage (with units like hectares, acres, sq ft), 7) Property address, 8) Contact person name, 9) Any other relevant details. IMPORTANT: Separate price (monetary value) from lot size (area with units). Translate all extracted information to English and return the information in a structured JSON format with clear field names in English.');

      const saleSignResponse = await fetch(apiUrl('/moondream/upload-and-analyze'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: saleSignFormData
      });

      const saleSignResult = await saleSignResponse.json();
      console.log('Sale sign analysis result:', saleSignResult);
      console.log('Response status:', saleSignResponse.status);
      console.log('Response ok:', saleSignResponse.ok);
      console.log('Full response structure:', JSON.stringify(saleSignResult, null, 2));

      if (saleSignResult.success) {
        // Extract data from sale sign
        const extractedData = extractSaleSignData(saleSignResult);
        console.log('Extracted sale sign data:', extractedData);
        
        const result = {
          success: true,
          data: {
            saleSign: saleSignResult,
            extractedData: extractedData
          }
        };

        setAnalysisResult(result.data);
        toast.success(t('reportPage.toasts.saleSignAnalysisComplete'), { id: 'analysis' });
        
        // Auto-fill form with extracted data
        autoFillForm(extractedData);
      } else {
        throw new Error(saleSignResult.message || 'Sale sign analysis failed');
      }

    } catch (error) {
      console.error('Sale sign analysis error:', error);
      setAnalysisError(error.message);
      
      // Check if it's a timeout error
      if (error.message.includes('timeout') || error.message.includes('Failed to analyze')) {
        toast.error(t('reportPage.toasts.aiTemporarilyUnavailable'), { id: 'analysis', duration: 5000 });
      } else {
        toast.error(t('reportPage.toasts.saleSignAnalysisFailed', { message: error.message }), { id: 'analysis' });
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Extract data from sale sign analysis result
  const extractSaleSignData = (saleSignResult) => {
    const extractedData = {
      sale_type: null,
      price: null,
      currency: null,
      phone_numbers: [],
      phone_number: null,
      email: null,
      whatsapp_number: null,
      lot_size: null,
      size_unit: null,
      address: null,
      contact_name: null
    };

    console.log('Checking saleSignResult:', saleSignResult);
    console.log('saleSignResult.success:', saleSignResult.success);
    console.log('saleSignResult.data:', saleSignResult.data);
    console.log('Type of saleSignResult.data:', typeof saleSignResult.data);
    
    if (saleSignResult.success && saleSignResult.data) {
      try {
        // The actual JSON data is nested deeper: saleSignResult.data.data
        const jsonString = saleSignResult.data.data || saleSignResult.data;
        const saleSignData = JSON.parse(jsonString);
        console.log('Parsed sale sign JSON:', saleSignData);
        console.log('lot_size_acreage field:', saleSignData.lot_size_acreage);
        
        if (saleSignData.sale_type) extractedData.sale_type = saleSignData.sale_type.toLowerCase();
        if (saleSignData.property_type && !extractedData.sale_type) {
          // Often comes as "For Sale" / "For Rent"
          const pt = String(saleSignData.property_type).toLowerCase();
          if (pt.includes('rent')) extractedData.sale_type = 'for rent';
          if (pt.includes('sale')) extractedData.sale_type = 'for sale';
        }

        const hasCurrencyIndicator = (text) => {
          if (!text) return false;
          const currencyRegex = /[$€¥₽₩₹₱₡₦₪₫]|usd|eur|cny|yuan|rmb|yen|mxn|cad|aud|chf|gbp|pounds?|dollars?|soles?|pesos?/i;
          return currencyRegex.test(text);
        };

        if (saleSignData.price_currency) {
          extractedData.currency = saleSignData.price_currency;
        }
        if (saleSignData.currency && !extractedData.currency) {
          extractedData.currency = saleSignData.currency;
        }

        if (saleSignData.price_value && saleSignData.price_currency) {
          const numericPrice = extractNumericValue(saleSignData.price_value);
          if (numericPrice !== null) {
            extractedData.price = numericPrice;
          }
        } else if (saleSignData.price) {
          // Newer AI shape: { value, unit } (unit is currency)
          if (typeof saleSignData.price === 'object' && saleSignData.price.value && (saleSignData.price.currency || saleSignData.price.unit)) {
            const numericPrice = extractNumericValue(saleSignData.price.value);
            if (numericPrice !== null) {
              extractedData.price = numericPrice;
              extractedData.currency = saleSignData.price.currency || saleSignData.price.unit;
            }
          } else if (typeof saleSignData.price === 'string' && hasCurrencyIndicator(saleSignData.price)) {
            const priceMatch = saleSignData.price.match(/([-+]?\d+(?:[.,]\d{3})*(?:[.,]\d+)?)/);
            if (priceMatch) {
              const numericPrice = extractNumericValue(priceMatch[1]);
              if (numericPrice !== null) {
                extractedData.price = numericPrice;
              }
            }
            if (!extractedData.currency) {
              extractedData.currency = hasCurrencyIndicator(saleSignData.price) ? saleSignData.price : null;
            }
          }
        }

        if (saleSignData.price_currency && !extractedData.currency) {
          extractedData.currency = saleSignData.price_currency;
        }
        if (Array.isArray(saleSignData.phone_numbers) && saleSignData.phone_numbers.length > 0) {
          extractedData.phone_numbers = saleSignData.phone_numbers.filter(Boolean).map((phone) => phone.toString().trim());
          extractedData.phone_number = extractedData.phone_numbers[0];
        } else if (saleSignData.phone_number) {
          const phoneValue = typeof saleSignData.phone_number === 'object' && saleSignData.phone_number.value
            ? saleSignData.phone_number.value
            : saleSignData.phone_number;
          extractedData.phone_numbers = [phoneValue].filter(Boolean).map((phone) => phone.toString().trim());
          extractedData.phone_number = extractedData.phone_numbers[0] || null;
        }
        if (saleSignData.price_currency && !extractedData.currency) {
          extractedData.currency = saleSignData.price_currency;
        }
        if (saleSignData.email_address) {
          if (typeof saleSignData.email_address === 'object' && saleSignData.email_address.value) {
            extractedData.email = saleSignData.email_address.value;
          } else {
            extractedData.email = saleSignData.email_address;
          }
        }
        if (saleSignData.whatsapp_number) {
          extractedData.whatsapp_number = typeof saleSignData.whatsapp_number === 'object' && saleSignData.whatsapp_number.value
            ? saleSignData.whatsapp_number.value
            : saleSignData.whatsapp_number;
        }

        // Newer AI keys for contact info
        const contactObj = saleSignData.contact_person || saleSignData.contact || saleSignData.contact_persons || null;
        if (contactObj && typeof contactObj === 'object') {
          if (!extractedData.contact_name && contactObj.name) extractedData.contact_name = contactObj.name;
          const phone = contactObj.phone || contactObj.phone_number;
          if (!extractedData.phone_number && phone) {
            const v = typeof phone === 'object' && phone.value ? phone.value : phone;
            extractedData.phone_numbers = [String(v)].filter(Boolean);
            extractedData.phone_number = extractedData.phone_numbers[0] || null;
          }
          if (!extractedData.email && contactObj.email) extractedData.email = contactObj.email;
        }
        const additionalInfo = saleSignData.additional_info || saleSignData.additional_details || null;
        if (additionalInfo && typeof additionalInfo === 'object') {
          if (!extractedData.email && additionalInfo.email) extractedData.email = additionalInfo.email;
          if (!extractedData.whatsapp_number && additionalInfo.whatsapp) extractedData.whatsapp_number = additionalInfo.whatsapp;
        }

        // Newer AI lot_size object: { value, unit }
        if (!saleSignData.lot_size_acreage && saleSignData.lot_size && typeof saleSignData.lot_size === 'object') {
          const v = saleSignData.lot_size.value ?? saleSignData.lot_size.size ?? null;
          const u = saleSignData.lot_size.unit ?? saleSignData.lot_size.units ?? null;
          const numericLot = extractNumericValue(v);
          if (numericLot !== null) extractedData.lot_size = numericLot;
          if (u) extractedData.size_unit = String(u).toLowerCase();
        }
        if (saleSignData.lot_size_acreage) {
          // Parse lot size to extract numeric value and unit
          const lotSizeText = saleSignData.lot_size_acreage;
          console.log('Parsing lot size:', lotSizeText);
          const sizeMatch = lotSizeText.match(/(\d+(?:\.\d+)?)\s*(HECTÁREAS?|HECTARES?|ACRES?|SQ\.?\s*FT\.?|SQUARE\s+FEET?|SQ\.?\s*M|SQUARE\s+METERS?|SQ\.?\s*MI|SQUARE\s+MILES?)/i);
          if (sizeMatch) {
            extractedData.lot_size = parseFloat(sizeMatch[1]);
            const unit = sizeMatch[2].toLowerCase();
            console.log('Size match found:', sizeMatch[1], 'unit:', unit);
            if (unit.includes('hectáreas') || unit.includes('hectares')) {
              extractedData.size_unit = 'hectare';
            } else if (unit.includes('acres')) {
              extractedData.size_unit = 'acre';
            } else if (unit.includes('sq') && unit.includes('ft')) {
              extractedData.size_unit = 'sq_ft';
            } else if (unit.includes('sq') && unit.includes('m') && !unit.includes('mi')) {
              extractedData.size_unit = 'sq_m';
            } else if (unit.includes('sq') && unit.includes('mi')) {
              extractedData.size_unit = 'sq_mi';
            } else {
              extractedData.size_unit = 'sq_m'; // default
            }
            console.log('Parsed lot size:', extractedData.lot_size, 'unit:', extractedData.size_unit);
          } else {
            // Fallback: try to extract just the number
            const numberMatch = lotSizeText.match(/(\d+(?:\.\d+)?)/);
            if (numberMatch) {
              extractedData.lot_size = parseFloat(numberMatch[1]);
              extractedData.size_unit = 'sq_m'; // default unit
              console.log('Number fallback:', extractedData.lot_size, 'unit:', extractedData.size_unit);
            } else {
              // Keep unparsed text separately so we don't break numeric inputs / backend validation.
              extractedData.lot_size = null;
              extractedData.lot_size_text = String(lotSizeText);
              console.log('Text fallback (unparsed lot size):', extractedData.lot_size_text);
            }
          }
        }
        if (saleSignData.property_address) extractedData.address = saleSignData.property_address;
        if (saleSignData.contact_person_name && !extractedData.contact_name) extractedData.contact_name = saleSignData.contact_person_name;
      } catch (e) {
        console.log('Sale sign data not in JSON format, using text extraction');
        // Fallback to text extraction if JSON parsing fails
        const text = String(saleSignResult.data || '');
        if (text.includes('For Sale')) extractedData.sale_type = 'for sale';
        if (text.includes('For Rent')) extractedData.sale_type = 'for rent';
        if (text.includes('For Lease')) extractedData.sale_type = 'for lease';
      }
    }

    return extractedData;
  };

  // Extract structured data from analysis results
  const extractStructuredData = (saleSignResult, propertyPhotoResults) => {
    const extractedData = {
      sale_type: null,
      price: null,
      phone_number: null,
      email: null,
      whatsapp_number: null,
      lot_size: null,
      size_unit: null,
      address: null,
      contact_name: null,
      property_type: null,
      landscape_features: [],
      building_present: false,
      building_type: null,
      building_condition: null,
      property_size_estimation: null,
      surrounding_area: null,
      access_roads: null,
      notable_features: [],
      issues: []
    };

    // Extract from sale sign
    if (saleSignResult.success && saleSignResult.data) {
      try {
        const saleSignData = JSON.parse(saleSignResult.data);
        if (saleSignData.sale_type) extractedData.sale_type = saleSignData.sale_type.toLowerCase();
        if (saleSignData.price) extractedData.price = saleSignData.price;
        if (saleSignData.phone_number) extractedData.phone_number = saleSignData.phone_number;
        if (saleSignData.email_address) extractedData.email = saleSignData.email_address;
        if (saleSignData.whatsapp_number) extractedData.whatsapp_number = saleSignData.whatsapp_number;
        if (saleSignData.lot_size_acreage) {
          // Parse lot size to extract numeric value and unit
          const lotSizeText = saleSignData.lot_size_acreage;
          const sizeMatch = lotSizeText.match(/(\d+(?:\.\d+)?)\s*(HECTÁREAS?|HECTARES?|ACRES?|SQ\.?\s*FT\.?|SQUARE\s+FEET?|SQ\.?\s*M|SQUARE\s+METERS?|SQ\.?\s*MI|SQUARE\s+MILES?)/i);
          if (sizeMatch) {
            extractedData.lot_size = parseFloat(sizeMatch[1]);
            const unit = sizeMatch[2].toLowerCase();
            if (unit.includes('hectáreas') || unit.includes('hectares')) {
              extractedData.size_unit = 'hectare';
            } else if (unit.includes('acres')) {
              extractedData.size_unit = 'acre';
            } else if (unit.includes('sq') && unit.includes('ft')) {
              extractedData.size_unit = 'sq_ft';
            } else if (unit.includes('sq') && unit.includes('m') && !unit.includes('mi')) {
              extractedData.size_unit = 'sq_m';
            } else if (unit.includes('sq') && unit.includes('mi')) {
              extractedData.size_unit = 'sq_mi';
            } else {
              extractedData.size_unit = 'sq_m'; // default
            }
          } else {
            // Fallback: try to extract just the number
            const numberMatch = lotSizeText.match(/(\d+(?:\.\d+)?)/);
            if (numberMatch) {
              extractedData.lot_size = parseFloat(numberMatch[1]);
              extractedData.size_unit = 'sq_m'; // default unit
            } else {
              extractedData.lot_size = null;
              extractedData.lot_size_text = String(lotSizeText);
            }
          }
        }
        if (saleSignData.property_address) extractedData.address = saleSignData.property_address;
        if (saleSignData.contact_person_name) extractedData.contact_name = saleSignData.contact_person_name;
      } catch (e) {
        console.log('Sale sign data not in JSON format, using text extraction');
      }
    }

    // Extract from property photos
    propertyPhotoResults.forEach(photo => {
      if (photo.analysis.success && photo.analysis.data) {
        try {
          const photoData = JSON.parse(photo.analysis.data);
          if (photoData.land_type) extractedData.property_type = photoData.land_type.toLowerCase();
          if (photoData.building_presence && photoData.building_presence !== 'none') {
            extractedData.building_present = true;
            extractedData.building_type = photoData.building_presence.toLowerCase();
          }
          if (photoData.building_condition) extractedData.building_condition = photoData.building_condition.toLowerCase();
          if (photoData.landscape_features && photoData.landscape_features !== 'none') {
            extractedData.landscape_features.push(photoData.landscape_features.toLowerCase());
          }
          if (photoData.property_size_estimation) extractedData.property_size_estimation = photoData.property_size_estimation;
          if (photoData.surrounding_areas && photoData.surrounding_areas !== 'none') {
            extractedData.surrounding_area = photoData.surrounding_areas;
          }
          if (photoData.access_roads_pathways && photoData.access_roads_pathways !== 'none') {
            extractedData.access_roads = 'Yes';
          }
          if (photoData.notable_features_or_issues && photoData.notable_features_or_issues !== 'none') {
            extractedData.notable_features.push(photoData.notable_features_or_issues.toLowerCase());
          }
        } catch (e) {
          console.log('Photo data not in JSON format, using text extraction');
        }
      }
    });

    return extractedData;
  };

  // Auto-fill form with extracted data
  const autoFillForm = (extractedData) => {
    if (!extractedData) return;

    console.log('Auto-filling form with extracted data:', extractedData);

    const newFormData = { ...formData };

    if (extractedData.sale_type) {
      const saleTypeText = extractedData.sale_type.toLowerCase();
      if (saleTypeText.includes('rent')) {
        newFormData.saleType = 'for_rent';
      } else if (saleTypeText.includes('owner')) {
        newFormData.saleType = 'for_sale_by_owner';
      } else {
        newFormData.saleType = 'for_sale';
      }
      newFormData.isOwner = newFormData.saleType === 'for_sale_by_owner' ? 'no' : 'no';
    }

    if (extractedData.property_type) {
      const typeText = extractedData.property_type.toLowerCase();
      if (typeText.includes('apartment') || typeText.includes('condo')) {
        newFormData.propertyCategory = 'apartment';
      } else if (typeText.includes('house') || typeText.includes('home')) {
        newFormData.propertyCategory = 'house';
      } else {
        newFormData.propertyCategory = 'land_lot';
      }
    }

    if (extractedData.price) {
      const numericPrice = typeof extractedData.price === 'number' ? extractedData.price : parseFloat(extractedData.price);
      if (!isNaN(numericPrice)) {
        newFormData.price = numericPrice;
      }
    }

    if (extractedData.currency) {
      newFormData.currency = normalizeCurrency(extractedData.currency);
    }

    if (extractedData.phone_number) {
        newFormData.phone = normalizePhone(extractedData.phone_number);
    }

    if (extractedData.whatsapp_number) {
        newFormData.whatsapp = normalizePhone(extractedData.whatsapp_number);
    }

    if (extractedData.email) {
      newFormData.email = extractedData.email;
    }

    if (extractedData.contact_name) {
      newFormData.contactName = extractedData.contact_name;
    }

    {
      const lotSizeNumeric = extractNumericValue(extractedData.lot_size);
      if (lotSizeNumeric !== null) {
        newFormData.lotSizeValue = String(lotSizeNumeric);
      }
    }

    if (extractedData.size_unit) {
      newFormData.lotSizeUnit = normalizeLotUnitForForm(extractedData.size_unit);
    }

    let description = formData.description ? `${formData.description}\n` : '';
    if (extractedData.address) {
      description += `Address: ${extractedData.address}\n`;
    }
    if (extractedData.property_type) {
      description += `Property Type: ${extractedData.property_type}\n`;
    }
    if (extractedData.building_present) {
      description += `Building: ${extractedData.building_type || 'Structure present'}\n`;
    }
    if (extractedData.building_condition) {
      description += `Condition: ${extractedData.building_condition}\n`;
    }
    if (extractedData.landscape_features && extractedData.landscape_features.length > 0) {
      description += `Landscape: ${extractedData.landscape_features.join(', ')}\n`;
    }
    if (extractedData.notable_features && extractedData.notable_features.length > 0) {
      description += `Features: ${extractedData.notable_features.join(', ')}\n`;
    }
    if (extractedData.surrounding_area) {
      description += `Surrounding Area: ${extractedData.surrounding_area}\n`;
    }
    if (extractedData.access_roads) {
      description += `Access: ${extractedData.access_roads}\n`;
    }
    if (extractedData.property_size_estimation) {
      description += `Size estimate: ${extractedData.property_size_estimation}\n`;
    }
    if (extractedData.lot_size_text) {
      description += `Lot size (unparsed): ${extractedData.lot_size_text}\n`;
    }
    if (extractedData.issues && extractedData.issues.length > 0) {
      description += `Issues: ${extractedData.issues.join(', ')}\n`;
    }

    if (description) {
      newFormData.description = description.trim();
    }

    console.log('Updated form data:', newFormData);
    setFormData(newFormData);
  };

  function normalizeSaleType(value) {
    const text = value ? value.toString().toLowerCase() : '';
    if (text.includes('rent')) return 'for_rent';
    if (text.includes('owner')) return 'for_sale_by_owner';
    return 'for_sale';
  }

  function normalizePropertyCategory(value) {
    const text = value ? value.toString().toLowerCase() : '';
    if (text.includes('apartment') || text.includes('condo') || text.includes('flat')) return 'apartment';
    if (text.includes('house') || text.includes('home') || text.includes('villa')) return 'house';
    return 'land_lot';
  }

  function normalizeCurrency(value) {
    const text = value ? value.toString().toLowerCase() : '';
    if (text.includes('eur') || text.includes('€')) return 'EUR';
    if (text.includes('cny') || text.includes('¥') || text.includes('yuan')) return 'CNY';
    return 'USD';
  }

  function normalizePhone(value) {
    if (!value) return '';
    const digits = value.toString().replace(/[^0-9+]/g, '');

    if (!digits.startsWith('+')) {
      if (digits.length >= 8 && digits.length <= 10) {
        return `+507${digits}`;
      }
      if (digits.length > 0) {
        return `+${digits}`;
      }
    }

    return digits;
  }

  function extractNumericValue(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const cleaned = value.toString().replace(/[^0-9.,-]/g, '').replace(/,/g, '');
    if (!cleaned) return null;
    const numeric = parseFloat(cleaned);
    return Number.isNaN(numeric) ? null : numeric;
  }

  // Return '' when value is not a valid number so FormData submits "not provided"
  // and Laravel's nullable numeric validators don't fail.
  function numericFormValue(value) {
    const numeric = extractNumericValue(value);
    return numeric === null ? '' : String(numeric);
  }

  const applyPayloadToForm = (prev, payload) => {
    const next = { ...prev };
    let label = null;

    switch (payload.field) {
      case 'sale_type': {
        const normalized = normalizeSaleType(payload.value);
        next.saleType = normalized;
        next.isOwner = normalized === 'for_sale_by_owner' ? 'no' : 'no';
        label = 'Sale type';
        break;
      }
      case 'property_category': {
        const normalized = normalizePropertyCategory(payload.value);
        next.propertyCategory = normalized;
        if (normalized === 'land_lot') {
          next.livingAreaValue = '';
          next.bedrooms = '0';
          next.bathrooms = '0';
          next.yearBuilt = '';
        }
        label = 'Property type';
        break;
      }
      case 'contact_name': {
        next.contactName = payload.value ? String(payload.value).trim() : '';
        label = 'Contact name';
        break;
      }
      case 'phone': {
        next.phone = payload.value ? normalizePhone(payload.value) : '';
        label = 'Phone number';
        break;
      }
      case 'whatsapp': {
        next.whatsapp = payload.value ? normalizePhone(payload.value) : '';
        label = 'WhatsApp';
        break;
      }
      case 'email': {
        next.email = payload.value ? String(payload.value).trim() : '';
        label = 'Email';
        break;
      }
      case 'price': {
        const numeric = extractNumericValue(payload.value);
        next.price = numeric !== null ? numeric : (payload.value ? String(payload.value).trim() : '');
        const currencySource = payload.currency || payload.value;
        if (currencySource) {
          next.currency = normalizeCurrency(currencySource);
        }
        label = 'Price';
        break;
      }
      case 'lot_size': {
        const numeric = extractNumericValue(payload.value);
        // Never put non-numeric text into the lot size input; it causes browser warnings
        // and will fail backend validation if submitted.
        next.lotSizeValue = numeric !== null ? String(numeric) : '';
        if (payload.unit) {
          next.lotSizeUnit = normalizeLotUnitForForm(payload.unit);
        }
        label = 'Lot size';
        break;
      }
      case 'description': {
        const text = payload.value ? String(payload.value).trim() : '';
        if (text) {
          next.description = text;
          label = 'Description';
        }
        break;
      }
      case 'address': {
        const text = payload.value ? String(payload.value).trim() : '';
        if (text) {
          const existing = (prev.description || '').trim();
          next.description = existing ? `${text}\n${existing}` : text;
          label = 'Description';
        }
        break;
      }
      default:
        break;
    }

    return { next, label };
  };

  const handleDragStart = (event, payload) => {
    event.dataTransfer.setData('text/plain', JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleFieldDrop = (event, allowedFields) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData('text/plain');
    if (!raw) return;

    try {
      const payload = JSON.parse(raw);
      if (allowedFields && allowedFields.length > 0 && !allowedFields.includes(payload.field)) {
        toast.error(t('reportPage.toasts.valueNotAllowed'));
        return;
      }

      let dropResult;
      setFormData((prev) => {
        const result = applyPayloadToForm(prev, payload);
        dropResult = result;
        return result.next;
      });

      if (dropResult?.label) {
        toast.success(t('reportPage.toasts.updatedFromAi', { label: dropResult.label }));
      }
    } catch (error) {
      console.error('Failed to apply dragged value', error);
    }
  };

  const clearField = (field, defaultValue = '') => {
    setFormData((prev) => ({ ...prev, [field]: defaultValue }));
  };

  const handleMapClick = async (latlng) => {
    try {
      // Check for nearby properties
      const response = await fetch(apiUrl('/properties/check-nearby'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          latitude: latlng.lat,
          longitude: latlng.lng,
          radius: 1 // 1km radius
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data && data.data.length > 0) {
          // Found nearby properties, show conflict dialog
          setConflictProperty(data.data[0]);
          setSelectedLocation(latlng);
          setSelectedLocationMarker({
            id: 'selected-location',
            lat: latlng.lat,
            lng: latlng.lng,
            title: t('reportPage.map.selectedLocationTitle'),
            description: t('reportPage.map.selectedLocationDesc'),
            color: '#3B82F6' // Blue color for all locations
          });
          setShowConflictDialog(true);
        } else {
          // No conflict, proceed with location selection
          setSelectedLocation(latlng);
          setSelectedLocationMarker({
            id: 'selected-location',
            lat: latlng.lat,
            lng: latlng.lng,
            title: t('reportPage.map.selectedLocationTitle'),
            description: t('reportPage.map.selectedLocationDesc'),
            color: '#3B82F6' // Blue color for all locations
          });
          openPropertyFormStep();
        }
      } else {
        // No conflict, proceed with location selection
        setSelectedLocation(latlng);
        setSelectedLocationMarker({
          id: 'selected-location',
          lat: latlng.lat,
          lng: latlng.lng,
          title: t('reportPage.map.selectedLocationTitle'),
          description: t('reportPage.map.selectedLocationDesc'),
          color: '#3B82F6' // Blue color for all locations
        });
        openPropertyFormStep();
      }
    } catch (error) {
      console.error('Error checking nearby properties:', error);
      toast.error(t('reportPage.toasts.errorCheckingNearby'));
    }
  };

  const handleConflictResponse = (isSameProperty, allowNewPin) => {
    setShowConflictDialog(false);
    
    if (isSameProperty && !allowNewPin) {
      toast.error(t('reportPage.toasts.cannotPlacePinOnExisting'));
      setSelectedLocationMarker(null);
      return;
    }
    
    if (isSameProperty && allowNewPin) {
      toast(t('reportPage.toasts.canPlaceSecondPin'), { icon: 'ℹ️' });
    }
    
    openPropertyFormStep();
  };

  const handleExpandOptionalSection = () => {
    if (!showOptionalFields) {
      const hasPhoneOrWhatsapp = (String(formData.phone ?? '').trim().length > 0)
        || (String(formData.whatsapp ?? '').trim().length > 0);
      if (!hasPhoneOrWhatsapp) {
        toast.error(t('reportPage.toasts.enterPhoneOrWhatsapp'));
        return;
      }
      if (formData.saleType === 'for_sale_by_owner' && formData.isOwner === 'yes' && formData.allowAgentContact === null) {
        toast.error(t('reportPage.toasts.answerAgentContact'));
        return;
      }
      return;
    }

    setFormOptionalExpanded(true);
    requestAnimationFrame(() => {
      optionalFormSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!formOptionalExpanded) {
      handleExpandOptionalSection();
      return;
    }
    
    if (!selectedLocation) {
      toast.error(t('reportPage.toasts.selectLocationOnMap'));
      return;
    }

    const hasPhone = String(formData.phone ?? '').trim().length > 0;
    const hasWhatsapp = String(formData.whatsapp ?? '').trim().length > 0;
    if (!hasPhone && !hasWhatsapp) {
      toast.error(t('reportPage.toasts.enterPhoneOrWhatsapp'));
      return;
    }

    if (formData.saleType === 'for_sale_by_owner' && formData.isOwner === 'yes' && formData.allowAgentContact === null) {
      toast.error(t('reportPage.toasts.answerAgentContact'));
      return;
    }

    if (propertyPhotos.length < MIN_PROPERTY_PHOTOS) {
      toast.error(t('reportPage.toasts.takeMinPropertyPhotos', { min: MIN_PROPERTY_PHOTOS }));
      return;
    }

    try {
      setIsSubmitting(true);
      setUploadProgress(0);
      const formDataToSend = new FormData();
      const finalLocationSource = locationSource === 'user' ? 'manual' : 'gps';
      const propertyTypeForBackend = formData.saleType === 'for_rent' ? 'rent' : 'sale';
      
      // Determine primary location based on active button
      const primaryLocation = locationSource === 'user' && userLocation ? userLocation : (gpsLocation || selectedLocation);

      const generateListingTitle = () => {
        const categoryLabel = formData.propertyCategory === 'apartment'
          ? 'Apartment'
          : formData.propertyCategory === 'house'
            ? 'House'
            : formData.propertyCategory === 'commercial'
              ? 'Commercial'
              : formData.propertyCategory === 'other'
                ? 'Other'
                : 'Land / Lot';
        const saleLabel = formData.saleType === 'for_rent'
          ? 'For rent'
          : formData.saleType === 'for_sale_by_owner'
            ? 'For sale by owner'
            : 'For sale';
        return `${categoryLabel} - ${saleLabel}`;
      };
      const listingTitle = generateListingTitle();
      const canonicalLotUnit = mapLotUnitForBackend(formData.lotSizeUnit);
      const canonicalLivingUnit = formData.livingAreaUnit
        ? mapLotUnitForBackend(formData.livingAreaUnit)
        : '';

      // Add property data
      formDataToSend.append('title', listingTitle);
      formDataToSend.append('description', formData.description);
      // Use primary location (based on active button)
      formDataToSend.append('latitude', primaryLocation.lat);
      formDataToSend.append('longitude', primaryLocation.lng);
      formDataToSend.append('client_reference', localPropertyReference);
      
      // Always store both GPS and user locations if available
      if (gpsLocation) {
        formDataToSend.append('gps_latitude', gpsLocation.lat);
        formDataToSend.append('gps_longitude', gpsLocation.lng);
      }
      if (userLocation) {
        formDataToSend.append('manual_latitude', userLocation.lat);
        formDataToSend.append('manual_longitude', userLocation.lng);
      }
      formDataToSend.append('location_source', finalLocationSource);
      formDataToSend.append('price', numericFormValue(formData.price));
      formDataToSend.append('currency', formData.currency || 'USD');
      formDataToSend.append('size', numericFormValue(formData.lotSizeValue));
      formDataToSend.append('size_unit', canonicalLotUnit);
      formDataToSend.append('property_type', propertyTypeForBackend);
      formDataToSend.append('phone', String(formData.phone ?? ''));
      formDataToSend.append('email', formData.email || '');
    formDataToSend.append('whatsapp', formData.whatsapp || '');

      // Additional metadata (optional fields for backend enrichment)
      formDataToSend.append('contact_name', formData.contactName || '');
      formDataToSend.append('property_category', formData.propertyCategory);
      formDataToSend.append('sale_type_detail', formData.saleType);
      formDataToSend.append('is_owner', formData.isOwner);
      formDataToSend.append('allow_agent_contact', formData.saleType === 'for_sale_by_owner' && formData.isOwner === 'yes' && formData.allowAgentContact === true ? '1' : '0');
      formDataToSend.append('has_title', formData.hasTitle || '');
      if (formData.plotNumber?.trim()) {
        formDataToSend.append('plot_number', formData.plotNumber.trim());
      }
      if (formData.roadAccess) formDataToSend.append('road_access', formData.roadAccess);
      if (formData.roadSurface) formDataToSend.append('road_surface', formData.roadSurface);
      if (formData.inCommunity) formDataToSend.append('in_community', formData.inCommunity);
      if (formData.waterConnection) {
        formDataToSend.append('water_connection', formData.waterConnection);
      }
      if (formData.electricity) {
        formDataToSend.append('electricity', formData.electricity);
      }
      formDataToSend.append('lot_size_value', numericFormValue(formData.lotSizeValue));
      formDataToSend.append('lot_size_unit', formData.lotSizeUnit || 'sqm');
      formDataToSend.append('living_area_value', numericFormValue(formData.livingAreaValue));
      formDataToSend.append('living_area_unit', canonicalLivingUnit);
      formDataToSend.append('bedrooms', numericFormValue(formData.bedrooms));
      formDataToSend.append('bathrooms', numericFormValue(formData.bathrooms));
      formDataToSend.append('year_built', numericFormValue(formData.yearBuilt));
      
      // Add media files - backend expects 'images' array
      mediaFiles.forEach((file, index) => {
        formDataToSend.append(`images[${index}]`, file);
      });

      const uploadProperty = () =>
        new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', apiUrl('/properties'), true);
          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percent = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(percent);
            }
          };
          xhr.onload = () => {
            resolve({
              status: xhr.status,
              responseText: xhr.responseText,
              headers: xhr.getAllResponseHeaders(),
            });
          };
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(formDataToSend);
        });

      const uploadResponse = await uploadProperty();

      console.log('Response status:', uploadResponse.status);
      console.log('Response headers:', uploadResponse.headers);

      const responseOk = uploadResponse.status >= 200 && uploadResponse.status < 300;
      if (responseOk) {
        const result = JSON.parse(uploadResponse.responseText || '{}');
        console.log('Success response:', result);
        toast.success(t('reportPage.toasts.submittedSuccessfully'));
        navigate('/reporter/dashboard');
      } else {
        let errorMessage = t('reportPage.toasts.failedToSubmit');
        
        try {
          const errorData = JSON.parse(uploadResponse.responseText || '{}');
          console.error('Error response:', errorData);
          if (errorData.errors && typeof errorData.errors === 'object') {
            console.error('Validation error keys:', Object.keys(errorData.errors));
            console.error('Validation error details:', errorData.errors);
          }
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            // Handle validation errors
            const errorMessages = Object.values(errorData.errors).flat();
            errorMessage = `Validation errors: ${errorMessages.join(', ')}`;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          errorMessage = `Server error (${uploadResponse.status})`;
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting property:', error);
      
      let errorMessage = t('reportPage.toasts.failedToSubmit');
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = t('reportPage.toasts.networkError');
      } else if (error.message) {
        errorMessage = t('reportPage.toasts.errorWithMessage', { message: error.message });
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };


  if (!isAuthenticated || user?.role !== 'land_reporter') {
    return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header: on mobile only a small back button to start page */}
        {isMobile ? (
          <div className="flex items-center mb-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900 py-2 pr-3 -ml-1 rounded-lg hover:bg-gray-100"
              aria-label={t('home.backToHome')}
            >
              <span className="text-xl" aria-hidden>←</span>
              <span>{t('common.back')}</span>
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">🏗️ {t('reportPage.header.title')}</h1>
                <p className="text-gray-600">{t('reportPage.header.subtitle')}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => navigate('/reporter/dashboard')}
                  className="btn-secondary px-4 py-2 rounded-lg"
                >
                  📊 {t('reportPage.header.dashboard')}
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="btn-secondary px-4 py-2 rounded-lg"
                >
                  🏠 {t('navigation.home')}
                </button>
                <button
                  onClick={() => logout()}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                >
                  🚪 {t('navigation.logout')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps — responsive: fits narrow phones without horizontal overflow */}
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-6 mb-6 overflow-hidden">
          <div className="flex w-full min-w-0 items-center justify-between gap-1 sm:gap-3">
            <div
              className={`flex min-w-0 flex-[1_1_0] basis-0 flex-col items-center gap-1 text-center sm:flex-row sm:gap-2 ${
                step === 'location' ? 'text-blue-600' : step === 'camera' || step === 'form' ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              <div
                className={`h-7 w-7 shrink-0 rounded-full text-sm sm:h-8 sm:w-8 sm:text-base flex items-center justify-center ${
                  step === 'location' ? 'bg-blue-600 text-white' : step === 'camera' || step === 'form' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}
              >
                🗺️
              </div>
              <span className="max-w-full px-0.5 text-[11px] font-medium leading-tight sm:text-sm">
                {t('reportPage.progress.location')}
              </span>
            </div>
            <div
              className={`mx-0.5 h-0.5 min-w-[8px] max-w-[32px] flex-1 shrink sm:h-1 ${step === 'camera' || step === 'form' ? 'bg-green-600' : 'bg-gray-300'}`}
              aria-hidden
            />
            <div
              className={`flex min-w-0 flex-[1_1_0] basis-0 flex-col items-center gap-1 text-center sm:flex-row sm:gap-2 ${
                step === 'camera' ? 'text-blue-600' : step === 'form' ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              <div
                className={`h-7 w-7 shrink-0 rounded-full text-sm sm:h-8 sm:w-8 sm:text-base flex items-center justify-center ${
                  step === 'camera' ? 'bg-blue-600 text-white' : step === 'form' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}
              >
                📷
              </div>
              <span className="max-w-full px-0.5 text-[11px] font-medium leading-tight sm:text-sm">
                {t('reportPage.progress.photos')}
              </span>
            </div>
            <div
              className={`mx-0.5 h-0.5 min-w-[8px] max-w-[32px] flex-1 shrink sm:h-1 ${step === 'form' ? 'bg-green-600' : 'bg-gray-300'}`}
              aria-hidden
            />
            <div className={`flex min-w-0 flex-[1_1_0] basis-0 flex-col items-center gap-1 text-center sm:flex-row sm:gap-2 ${step === 'form' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div
                className={`h-7 w-7 shrink-0 rounded-full text-sm sm:h-8 sm:w-8 sm:text-base flex items-center justify-center ${
                  step === 'form' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}
              >
                📝
              </div>
              <span className="max-w-full px-0.5 text-[11px] font-medium leading-tight sm:text-sm">
                {t('reportPage.progress.details')}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {step === 'location' && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">🗺️ {t('reportPage.location.title')}</h2>
              <p className="text-gray-600 mb-4">{t('reportPage.location.subtitle')}</p>
              
              {/* Location Status */}
              <div className="mb-6">
                {isGettingLocation ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                      <span className="text-blue-800 font-medium">{t('reportPage.location.gettingGps')}</span>
                    </div>
                  </div>
                ) : !currentLocation ? (
                  <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center">
                        <span className="text-orange-600 text-xl mr-3">📍</span>
                        <div>
                          <div className="font-semibold text-orange-800">{t('reportPage.location.gpsNotAvailableTitle')}</div>
                          <div className="text-sm text-orange-700">{t('reportPage.location.gpsNotAvailableBody')}</div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={getCurrentLocation}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                        >
                          {t('common.retry')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : selectedLocation ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {locationSource === 'user'
                          ? t('reportPage.location.userLocationManual')
                          : t('reportPage.location.gpsLocation')}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${locationSource === 'user' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                          {locationSource === 'user'
                            ? t('reportPage.location.badgeManual')
                            : t('reportPage.location.badgeGps')}
                        </span>
                        {gpsLocation && (
                          <button
                            onClick={getCurrentLocation}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-xs"
                            title={t('reportPage.location.refreshGpsTitle')}
                          >
                            🔄 {t('reportPage.location.refreshGps')}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-700">
                      {t('reportPage.common.latitude')}: {selectedLocation.lat.toFixed(6)}, {t('reportPage.common.longitude')}: {selectedLocation.lng.toFixed(6)}
                    </div>
                    {selectedAddress && (
                      <div className="mt-2 text-sm text-gray-700">
                        {selectedAddress.street && (
                          <div className="font-medium text-gray-900">{selectedAddress.street}</div>
                        )}
                        <div className="text-xs text-gray-600">
                          {[
                            selectedAddress.township,
                            selectedAddress.district,
                            selectedAddress.province,
                            selectedAddress.country,
                          ].filter(Boolean).join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Big Map */}
              <div className="mb-6 w-full" style={{ width: '100%' }}>
                <div className="h-96 border rounded-lg overflow-hidden shadow-lg w-full" style={{ width: '100%' }}>
                  <MapComponent
                    height="100%"
                    className="w-full"
                    style={{ width: '100%' }}
                    center={currentLocation ? [currentLocation.lat, currentLocation.lng] : undefined}
                    zoom={currentLocation ? 16 : 13}
                    markers={selectedLocationMarker
                      ? [{
                          id: 'property-location',
                          lat: selectedLocationMarker.lat,
                          lng: selectedLocationMarker.lng,
                          title: t('reportPage.map.markerTitle'),
                          description: t('reportPage.map.markerDescSelected'),
                          color: '#EF4444'
                        }]
                      : currentLocation
                        ? [{
                            id: 'property-location',
                            lat: currentLocation.lat,
                            lng: currentLocation.lng,
                            title: t('reportPage.map.markerTitle'),
                            description: t('reportPage.map.markerDescDetected'),
                            color: '#EF4444'
                          }]
                        : []}
                    onMapClick={(location) => {
                      setUserLocation(location);
                      setSelectedLocation(location);
                      setSelectedLocationMarker({
                        id: 'property-location',
                        lat: location.lat,
                        lng: location.lng,
                        title: t('reportPage.map.markerTitle'),
                        description: t('reportPage.map.markerDescUserSelected'),
                        color: '#EF4444'
                      });
                      setFocusOnLocation(location);
                      setLocationSource('user');
                      toast.success(t('reportPage.toasts.propertyLocationUpdated'), { id: 'manual-position-update' });
                    }}
                    onMarkerDrag={(marker) => {
                      const location = { lat: marker.lat, lng: marker.lng };
                      const updatedMarker = {
                        id: 'property-location',
                        lat: marker.lat,
                        lng: marker.lng,
                        title: t('reportPage.map.markerTitle'),
                        description: t('reportPage.map.markerDescUserSelected'),
                        color: '#EF4444'
                      };
                      setUserLocation(location);
                      setSelectedLocation(location);
                      setSelectedLocationMarker(updatedMarker);
                      setFocusOnLocation(location);
                      setLocationSource('user');
                      toast.success(t('reportPage.toasts.userLocationUpdated'), { id: 'manual-position-update' });
                    }}
                    allowClick={true}
                    showControls={true}
                    focusOn={focusOnLocation}
                    draggableMarkerId={selectedLocationMarker ? 'property-location' : null}
                  />
                </div>
                <p
                  className="text-sm mt-2 text-center text-gray-600"
                >
                  📍 {t('reportPage.location.dragHint')}
                </p>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between">
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    if (selectedLocation) {
                      setStep('camera');
                    } else {
                      toast.error(t('reportPage.toasts.setLocationBeforeProceeding'));
                    }
                  }}
                  disabled={!selectedLocation}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {t('reportPage.location.nextTakePhotos')}
                </button>
              </div>
            </div>
        )}

        {step === 'camera' && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">📷 {t('reportPage.camera.title')}</h2>
                <button
                  type="button"
                  onClick={() => setStep('location')}
                  className="btn-secondary px-4 py-2 rounded-lg text-sm"
                >
                  ← {t('reportPage.camera.backToLocation')}
                </button>
              </div>
              
              {/* Step Instructions */}
              {photoStep === 'sale_sign' && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">{t('reportPage.camera.instructions.step1Title')}</h3>
                  <p className="text-blue-700 mb-3">{t('reportPage.camera.instructions.step1Body')}</p>
                  <button
                    type="button"
                    onClick={handleSkipSaleSign}
                    className="text-sm font-medium text-blue-800 underline hover:text-blue-900"
                  >
                    {t('reportPage.camera.skipSaleSign')}
                  </button>
                </div>
              )}

              {photoStep === 'property_photos' && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">
                    {t('reportPage.camera.instructions.step2Title', { min: MIN_PROPERTY_PHOTOS, max: MAX_PROPERTY_PHOTOS })}
                  </h3>
                  <p className="text-green-700 mb-2">{t('reportPage.camera.instructions.step2Body')}</p>
                  <p className="text-green-700 text-sm font-medium">
                    {t('reportPage.camera.instructions.photosTaken', {
                      taken: propertyPhotos.length,
                      max: MAX_PROPERTY_PHOTOS,
                      min: MIN_PROPERTY_PHOTOS,
                    })}
                  </p>
                </div>
              )}

              {photoStep === 'optional_video' && (
                <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-800 mb-2">{t('reportPage.camera.instructions.step3Title')}</h3>
                  <p className="text-purple-700">{t('reportPage.camera.instructions.step3Body')}</p>
                </div>
              )}

              <CameraCapture
                key={photoStep}
                onCapture={handleMediaCapture}
                singleCapture={true}
                showPreview={false}
                autoStart
                compact
                captureMode={photoStep === 'optional_video' ? 'video' : 'photo'}
              />

              {(saleSignPhoto || propertyPhotos.length > 0 || optionalVideo) && (
                <div className="mb-6 mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">{t('reportPage.camera.capturedMediaTitle')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {saleSignPhoto && saleSignPreview && (
                      <div className="relative group border rounded-lg overflow-hidden shadow-sm">
                        <img src={saleSignPreview} alt={t('reportPage.camera.alt.saleSign')} className="w-full h-36 object-cover" />
                        <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">{t('reportPage.camera.steps.saleSign')}</div>
                        <button
                          type="button"
                          onClick={handleRemoveSaleSignPhoto}
                          className="absolute top-2 right-2 bg-white/90 text-red-600 rounded-full p-1 shadow hover:bg-white"
                          aria-label={t('reportPage.camera.aria.deleteSaleSign')}
                        >
                          🗑️
                        </button>
                      </div>
                    )}

                    {propertyPhotoPreviews.map((item, index) => (
                      <div key={index} className="relative group border rounded-lg overflow-hidden shadow-sm">
                        <img src={item.preview} alt={t('reportPage.camera.alt.propertyPhoto', { index: index + 1 })} className="w-full h-36 object-cover" />
                        <div className="absolute top-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                          {t('reportPage.camera.propertyNumber', { index: index + 1 })}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemovePropertyPhoto(index)}
                          className="absolute top-2 right-2 bg-white/90 text-red-600 rounded-full p-1 shadow hover:bg-white"
                          aria-label={t('reportPage.camera.aria.deletePropertyPhoto', { index: index + 1 })}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}

                    {optionalVideo && optionalVideoPreview && (
                      <div className="relative group border rounded-lg overflow-hidden shadow-sm">
                        <video
                          src={optionalVideoPreview}
                          className="w-full h-36 object-cover"
                          controls
                        />
                        <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                          {t('reportPage.camera.optionalVideo')}
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveOptionalVideo}
                          className="absolute top-2 right-2 bg-white/90 text-red-600 rounded-full p-1 shadow hover:bg-white"
                          aria-label={t('reportPage.camera.aria.deleteOptionalVideo')}
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <button
                  onClick={() => navigate('/reporter/dashboard')}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  ← {t('reportPage.camera.backToDashboard')}
                </button>

                <div className="flex flex-wrap gap-3 justify-end">
                  {photoStep === 'property_photos' && hasMinimumPropertyPhotos && (
                    <>
                      <button
                        onClick={skipVideo}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        {t('reportPage.camera.skipVideo')}
                      </button>
                      <button
                        onClick={() => setPhotoStep('optional_video')}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        {t('reportPage.camera.recordOptionalVideo')}
                      </button>
                    </>
                  )}

                  {photoStep === 'optional_video' && (
                    <button
                      onClick={skipVideo}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      {t('reportPage.camera.skipVideo')}
                    </button>
                  )}

                  {hasMinimumPropertyPhotos && photoStep !== 'sale_sign' && (
                    <button
                      onClick={proceedToLocation}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {t('reportPage.camera.continueToDetails')} →
                    </button>
                  )}
                </div>
              </div>
            </div>
        )}

        {step === 'form' && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">📝 {t('reportPage.form.title')}</h2>
              <button
                type="button"
                onClick={() => {
                  setFormOptionalExpanded(false);
                  setStep('camera');
                }}
                className="btn-secondary px-4 py-2 rounded-lg text-sm"
              >
                ← {t('reportPage.form.backToPhotos')}
              </button>
            </div>

            {selectedLocation && (
              <div className="mb-6">
                <div className="h-64 rounded-lg overflow-hidden border border-gray-200">
                  <MapComponent
                    height="100%"
                    center={[selectedLocation.lat, selectedLocation.lng]}
                    zoom={16}
                    markers={[{
                      id: 'property-location',
                      lat: selectedLocation.lat,
                      lng: selectedLocation.lng,
                      title: t('reportPage.map.markerTitle'),
                      description: t('reportPage.map.markerDescReported'),
                      color: '#EF4444'
                    }]}
                    allowClick={false}
                    draggableMarkerId={null}
                    focusOn={selectedLocation}
                    showControls={true}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mt-2">
                  <span>{t('reportPage.common.latitude')}: {selectedLocation.lat.toFixed(6)}</span>
                  <span>{t('reportPage.common.longitude')}: {selectedLocation.lng.toFixed(6)}</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${locationSource === 'user' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                    {locationSource === 'user' ? t('reportPage.form.manualSelection') : t('reportPage.form.gpsSelection')}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className="space-y-8">
              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">🤖 {t('reportPage.ai.title')}</h3>
                  {isAnalyzing && (
                    <div className="flex items-center space-x-2 text-blue-600 text-sm">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span>{t('reportPage.ai.analyzing')}</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {t('reportPage.ai.instructions')}
                </p>
                {analysisError && (
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                    ⚠️ {analysisError}
                  </div>
                )}
                <div className={`grid gap-3 ${aiChips.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                  {aiChips.map((chip, index) => (
                    <div
                      key={`${chip.field}-${index}`}
                      draggable
                      onDragStart={(event) => handleDragStart(event, chip)}
                      className="cursor-grab rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <div className="text-xs font-medium uppercase text-gray-500">{chip.label}</div>
                      <div className="text-sm font-semibold text-gray-900 truncate">{chip.displayValue}</div>
                      <div className="text-xs text-gray-400 mt-1">{t('reportPage.ai.dragHint')}</div>
                    </div>
                  ))}
                  {aiChips.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                      {t('reportPage.ai.emptyHint')}
                    </div>
                  )}
                </div>
              </section>

              <section className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('reportPage.form.basicsTitle')}</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.uniquePropertyId')}</label>
                    <input
                      type="text"
                      value={localPropertyReference}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-700"
                    />
                  </div>

                  <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['property_category'])}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.propertyType')} *</label>
                    <select
                      value={formData.propertyCategory}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          propertyCategory: value,
                          ...(value === 'land_lot'
                            ? { livingAreaValue: '', bedrooms: '0', bathrooms: '0', yearBuilt: '' }
                            : {
                              roadAccess: '',
                              roadSurface: '',
                              inCommunity: '',
                              waterConnection: '',
                              electricity: '',
                            }),
                        }));
                      }}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow ${requiredFieldClass(!!formData.propertyCategory)}`}
                    >
                      <option value="land_lot">{t('reportPage.form.propertyTypes.landLot')}</option>
                      <option value="apartment">{t('reportPage.form.propertyTypes.apartment')}</option>
                      <option value="house">{t('reportPage.form.propertyTypes.house')}</option>
                      <option value="commercial">{t('reportPage.form.propertyTypes.commercial')}</option>
                      <option value="other">{t('reportPage.form.propertyTypes.other')}</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragPropertyType')}</p>
                  </div>

                  <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['sale_type'])}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.saleType')} *</label>
                    <select
                        value={formData.saleType}
                        onChange={(e) => {
                          const nextSaleType = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            saleType: nextSaleType,
                            isOwner: nextSaleType === 'for_sale_by_owner' ? 'no' : 'no',
                            allowAgentContact: null,
                          }));
                        }}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow ${requiredFieldClass(!!formData.saleType)}`}
                    >
                      <option value="for_sale">{t('reportPage.form.saleTypes.forSale')}</option>
                      <option value="for_sale_by_owner">{t('reportPage.form.saleTypes.forSaleByOwner')}</option>
                      <option value="for_rent">{t('reportPage.form.saleTypes.forRent')}</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragSaleType')}</p>
                  </div>

                  {formData.saleType === 'for_sale_by_owner' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.isOwner')} *</label>
                      <select
                        value={formData.isOwner}
                        onChange={(e) => {
                          const next = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            isOwner: next,
                            allowAgentContact: next === 'yes' ? prev.allowAgentContact : null,
                          }));
                        }}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow ${requiredFieldClass(!!formData.isOwner)}`}
                      >
                        <option value="yes">{t('common.yes')}</option>
                        <option value="no">{t('common.no')}</option>
                      </select>
                    </div>
                  )}

                  {/* Modal: Einverständnis Maklerkontakt (when Verkauf vom Eigentümer + Ich bin Eigentümer) */}
                  {formData.saleType === 'for_sale_by_owner' && formData.isOwner === 'yes' && formData.allowAgentContact === null && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="agent-contact-modal-title">
                      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <h2 id="agent-contact-modal-title" className="text-lg font-semibold text-gray-900 mb-3">
                          {t('reportPage.modal.agentContactTitle')}
                        </h2>
                        <p className="text-gray-600 mb-6">
                          {t('reportPage.modal.agentContactBody')}
                        </p>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, allowAgentContact: true }))}
                            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                          >
                            {t('common.yes')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, allowAgentContact: false }))}
                            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                          >
                            {t('common.no')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['phone'])}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.phoneLabel')}</label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder={t('reportPage.form.placeholders.phone')}
                        className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow ${requiredFieldClass((String(formData.phone ?? '').trim().length > 0) || (String(formData.whatsapp ?? '').trim().length > 0))}`}
                      />
                      {formData.phone && (
                        <button
                          type="button"
                          onClick={() => clearField('phone')}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          {t('common.clear')}
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">{t('reportPage.form.phoneWhatsappRequirement')}</p>
                    <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragPhone')}</p>
                  </div>

                  <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['whatsapp'])}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.whatsappLabel')}</label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        value={formData.whatsapp}
                        onChange={(e) => setFormData((prev) => ({ ...prev, whatsapp: e.target.value }))}
                        placeholder={t('reportPage.form.placeholders.whatsapp')}
                        className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow ${requiredFieldClass((String(formData.phone ?? '').trim().length > 0) || (String(formData.whatsapp ?? '').trim().length > 0))}`}
                      />
                      {formData.whatsapp && (
                        <button
                          type="button"
                          onClick={() => clearField('whatsapp')}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          {t('common.clear')}
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">{t('reportPage.form.whatsappHint')}</p>
                  </div>
                </div>

              </section>

              {formOptionalExpanded && (
                <section ref={optionalFormSectionRef} className="space-y-6 scroll-mt-4">
                  <h3 className="text-lg font-semibold text-gray-900">{t('reportPage.form.optionalTitle')}</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2" onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['description', 'address'])}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.description')}</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                        rows={5}
                        placeholder={t('reportPage.form.placeholders.description')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                      />
                      <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragDescriptionOrAddress')}</p>
                    </div>
                    <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['contact_name'])}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.contactName')}</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.contactName}
                          onChange={(e) => setFormData((prev) => ({ ...prev, contactName: e.target.value }))}
                          placeholder={t('reportPage.form.placeholders.contactName')}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {formData.contactName && (
                          <button
                            type="button"
                            onClick={() => clearField('contactName')}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            {t('common.clear')}
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragContactName')}</p>
                    </div>

                    <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['email'])}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                          placeholder={t('reportPage.form.placeholders.email')}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {formData.email && (
                          <button
                            type="button"
                            onClick={() => clearField('email')}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            {t('common.clear')}
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragEmail')}</p>
                    </div>

                    <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.titleDeed')}</label>
                        <select
                          value={formData.hasTitle}
                          onChange={(e) => setFormData((prev) => ({ ...prev, hasTitle: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="yes">{t('common.yes')}</option>
                          <option value="no">{t('common.no')}</option>
                          <option value="not_sure">{t('reportPage.form.notSure')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.plotNumber')}</label>
                        <input
                          type="text"
                          value={formData.plotNumber}
                          onChange={(e) => setFormData((prev) => ({ ...prev, plotNumber: e.target.value }))}
                          placeholder={t('reportPage.form.placeholders.plotNumber')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-base font-semibold text-gray-900">{t('reportPage.form.accessTitle')}</h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.roadAccess')}</label>
                        <select
                          value={formData.roadAccess}
                          onChange={(e) => setFormData((prev) => ({ ...prev, roadAccess: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">{t('common.select')}</option>
                          <option value="yes">{t('common.yes')}</option>
                          <option value="no">{t('common.no')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.roadSurface')}</label>
                        <select
                          value={formData.roadSurface}
                          onChange={(e) => setFormData((prev) => ({ ...prev, roadSurface: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">{t('common.select')}</option>
                          <option value="asphalt">{t('reportPage.form.roadSurfaceAsphalt')}</option>
                          <option value="gravel">{t('reportPage.form.roadSurfaceGravel')}</option>
                          <option value="dirt">{t('reportPage.form.roadSurfaceDirt')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.inCommunity')}</label>
                        <select
                          value={formData.inCommunity}
                          onChange={(e) => setFormData((prev) => ({ ...prev, inCommunity: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">{t('common.select')}</option>
                          <option value="yes">{t('common.yes')}</option>
                          <option value="no">{t('common.no')}</option>
                          <option value="not_sure">{t('reportPage.form.notSure')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.waterConnection')}</label>
                        <select
                          value={formData.waterConnection}
                          onChange={(e) => setFormData((prev) => ({ ...prev, waterConnection: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">{t('common.select')}</option>
                          <option value="yes">{t('common.yes')}</option>
                          <option value="no">{t('common.no')}</option>
                          <option value="not_sure">{t('reportPage.form.notSure')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.electricity')}</label>
                        <select
                          value={formData.electricity}
                          onChange={(e) => setFormData((prev) => ({ ...prev, electricity: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">{t('common.select')}</option>
                          <option value="yes">{t('common.yes')}</option>
                          <option value="no">{t('common.no')}</option>
                          <option value="not_sure">{t('reportPage.form.notSure')}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-base font-semibold text-gray-900">{t('reportPage.form.dimensionsTitle')}</h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['lot_size'])}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.lotSize')}</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.lotSizeValue}
                            onChange={(e) => setFormData((prev) => ({ ...prev, lotSizeValue: e.target.value }))}
                            placeholder={t('reportPage.form.placeholders.lotSize')}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <select
                            value={formData.lotSizeUnit}
                            onChange={(e) => setFormData((prev) => ({ ...prev, lotSizeUnit: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                          >
                            <option value="sqm">{t('reportPage.units.sqm')}</option>
                            <option value="acres">{t('reportPage.units.acres')}</option>
                            <option value="hectares">{t('reportPage.units.hectares')}</option>
                          </select>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragLotSize')}</p>
                      </div>

                      {isResidentialProperty && (
                        <div className="space-y-4 md:col-span-2">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('reportPage.form.livingArea')}
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={formData.livingAreaValue}
                                  onChange={(e) => setFormData((prev) => ({ ...prev, livingAreaValue: e.target.value }))}
                                  placeholder={t('reportPage.form.placeholders.livingArea')}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <select
                                  value={formData.livingAreaUnit}
                                  onChange={(e) => setFormData((prev) => ({ ...prev, livingAreaUnit: e.target.value }))}
                                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                                >
                                  <option value="sqm">{t('reportPage.units.sqm')}</option>
                                  <option value="sqft">{t('reportPage.units.sqft')}</option>
                                </select>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('reportPage.form.bedrooms')}
                              </label>
                              <select
                                value={formData.bedrooms}
                                onChange={(e) => setFormData((prev) => ({ ...prev, bedrooms: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {Array.from({ length: 11 }).map((_, idx) => (
                                  <option key={`bedroom-${idx}`} value={String(idx)}>
                                    {idx}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('reportPage.form.bathrooms')}
                              </label>
                              <select
                                value={formData.bathrooms}
                                onChange={(e) => setFormData((prev) => ({ ...prev, bathrooms: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {Array.from({ length: 11 }).map((_, idx) => (
                                  <option key={`bathroom-${idx}`} value={String(idx)}>
                                    {idx}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('reportPage.form.yearBuilt')}
                              </label>
                              <input
                                type="number"
                                min="1800"
                                max={new Date().getFullYear()}
                                value={formData.yearBuilt}
                                onChange={(e) => setFormData((prev) => ({ ...prev, yearBuilt: e.target.value }))}
                                placeholder={t('reportPage.form.placeholders.yearBuilt')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div onDragOver={handleDragOver} onDrop={(event) => handleFieldDrop(event, ['price'])}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportPage.form.price')}</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
                        placeholder={t('reportPage.form.placeholders.price')}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData((prev) => ({ ...prev, currency: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="CNY">CNY</option>
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-400">{t('reportPage.aiHints.dragPrice')}</p>
                  </div>
                </section>
              )}

              {isSubmitting && (
                <div className="w-full">
                  <div className="mb-2 text-sm text-gray-600">
                    {t('common.loading')} {uploadProgress}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setFormOptionalExpanded(false);
                    setStep('camera');
                  }}
                  disabled={isSubmitting}
                  className="btn-secondary px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← {t('reportPage.form.backToPhotos')}
                </button>
                <div className="flex flex-wrap items-center gap-3">
                  {!formOptionalExpanded ? (
                    <button
                      type="button"
                      onClick={handleExpandOptionalSection}
                      disabled={isSubmitting}
                      className="btn-primary px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.next')} →
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setFormOptionalExpanded(false);
                          requestAnimationFrame(() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          });
                        }}
                        disabled={isSubmitting}
                        className="btn-secondary px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← {t('common.back')}
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? `${t('common.loading')}...` : t('reportPage.form.submit')} →
                      </button>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}

          {/* Conflict Dialog */}
          {showConflictDialog && conflictProperty && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                <h3 className="text-lg font-semibold mb-4">⚠️ {t('reportPage.conflict.title')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('reportPage.conflict.body')} <strong>{conflictProperty.title}</strong>
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => handleConflictResponse(true, false)}
                    className="w-full btn-secondary py-2 rounded-lg"
                  >
                    {t('reportPage.conflict.chooseDifferent')}
                  </button>
                  <button
                    onClick={() => handleConflictResponse(true, true)}
                    className="w-full btn-primary py-2 rounded-lg"
                  >
                    {t('reportPage.conflict.addUpdate')}
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default PropertyReportPage;